import React from "react";
import {
  Divider,
  HStack,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
} from "@chakra-ui/react";
import { DynamicFormStack } from "../common/ResponsiveFormStack";
import { Trans } from "@lingui/macro";
import { useProgramsQuery } from "../../hooks/useProgramsQuery";
import { PreviewDropdownFormControl } from "../common/FormControlComponents";
import { useLingui } from "@lingui/react";
import { ActivityChoice } from "../../types/activity.types";
import CurrencyContext from "../../context/CurrencyContext";
import { formatDateForInput } from "../../utils/date-formatter";
import { formatPrice } from "../../utils/price-formatter";
import {
  ModuleChangeStatus,
  ModuleSectionHeader,
} from "../common/ModuleToggleIndicator";

interface ProgramsProps {
  congressId: number;
  setFieldValue: Function;
  professionals: ActivityChoice[] | null;
  socials: ActivityChoice[] | null;
  isEditMode?: boolean;
  moduleChange?: ModuleChangeStatus;
}

enum ProgramChangeType {
  Professional = "professionals",
  Social = "socials",
}

const Programs: React.FC<ProgramsProps> = ({
  congressId,
  setFieldValue,
  professionals,
  socials,
  isEditMode = false,
  moduleChange = "unchanged",
}) => {
  const { i18n } = useLingui();
  const currency = React.useContext(CurrencyContext);
  const {
    socialList,
    professionalList,
    areSocialsLoading,
    areProfessionalsLoading,
  } = useProgramsQuery(congressId, isEditMode);

  const isSoftRequired = React.useMemo(
    () => isEditMode && moduleChange === "enabled",
    [isEditMode, moduleChange],
  );
  const hasAnyProfessionalSelected = React.useMemo(
    () =>
      professionals != null &&
      Array.isArray(professionals) &&
      professionals.some((p) => (p?.count ?? 0) > 0),
    [professionals],
  );
  const hasAnySocialSelected = React.useMemo(
    () =>
      socials != null &&
      Array.isArray(socials) &&
      socials.some((s) => (s?.count ?? 0) > 0),
    [socials],
  );

  React.useEffect(() => {
    if (socialList.length > 0) {
      // Merge existing values with new social list
      // If socials already has values, preserve them; otherwise initialize with 0
      const activityChoiceList: ActivityChoice[] = socialList.map(
        (activity) => {
          const existing =
            socials && Array.isArray(socials)
              ? socials.find((s) => s.activityId === activity.id)
              : undefined;
          return {
            activityId: activity.id,
            count: existing?.count ?? 0,
          };
        },
      );
      // Only set if the list structure changed (length or IDs)
      const currentSocials = socials && Array.isArray(socials) ? socials : [];
      const needsUpdate =
        currentSocials.length !== activityChoiceList.length ||
        activityChoiceList.some(
          (newSocial) =>
            !currentSocials.some((s) => s.activityId === newSocial.activityId),
        );
      if (needsUpdate) {
        setFieldValue(ProgramChangeType.Social, activityChoiceList);
      }
    }
  }, [socialList, setFieldValue]); // Removed socials from dependencies to avoid infinite loop

  React.useEffect(() => {
    if (professionalList.length > 0) {
      // Merge existing values with new professional list
      // If professionals already has values, preserve them; otherwise initialize with 0
      const activityChoiceList: ActivityChoice[] = professionalList.map(
        (activity) => {
          const existing =
            professionals && Array.isArray(professionals)
              ? professionals.find((p) => p.activityId === activity.id)
              : undefined;
          return {
            activityId: activity.id,
            count: existing?.count ?? 0,
          };
        },
      );
      // Only set if the list structure changed (length or IDs)
      const currentProfessionals =
        professionals && Array.isArray(professionals) ? professionals : [];
      const needsUpdate =
        currentProfessionals.length !== activityChoiceList.length ||
        activityChoiceList.some(
          (newProf) =>
            !currentProfessionals.some(
              (p) => p.activityId === newProf.activityId,
            ),
        );
      if (needsUpdate) {
        setFieldValue(ProgramChangeType.Professional, activityChoiceList);
      }
    }
  }, [professionalList, setFieldValue]); // Removed professionals from dependencies to avoid infinite loop

  return (
    <Stack data-testid="preview-activities-form">
      <ModuleSectionHeader
        title={<Trans>Activities</Trans>}
        change={moduleChange}
      />
      {professionalList.length && (
        <>
          <HStack>
            <Text fontSize="l" mb="2">
              <Trans>Professional Activities</Trans>
            </Text>
          </HStack>
          <DynamicFormStack testId="preview-professional-form">
            {professionalList.map((professional, index) => {
              const existingProfessional =
                professionals && Array.isArray(professionals)
                  ? professionals.find((p) => p.activityId === professional.id)
                  : undefined;
              const count = existingProfessional?.count ?? 0;
              const isProfessionalSoftRequired =
                isSoftRequired && !hasAnyProfessionalSelected && count === 0;
              const defaultPreviewText = isProfessionalSoftRequired
                ? i18n._("Originally not selected")
                : undefined;
              return (
                <PreviewDropdownFormControl
                  key={index}
                  id={`professional${index}`}
                  label={`${professional.name} ${formatDateForInput(
                    professional.date,
                  )}`}
                  isInactive={isEditMode && !professional.enabled}
                  values={[
                    { id: 0, leftText: i18n._("Not ordered") },
                    {
                      id: 1,
                      leftText: i18n._("1 person"),
                      rightText: `${formatPrice(professional.price)} ${currency}`,
                    },
                    {
                      id: 2,
                      leftText: i18n._("2 person"),
                      rightText: `${formatPrice(
                        professional.price * 2,
                      )} ${currency}`,
                    },
                    {
                      id: 3,
                      leftText: i18n._("3 person"),
                      rightText: `${formatPrice(
                        professional.price * 3,
                      )} ${currency}`,
                    },
                  ]}
                  fieldName={`professionals[${index}].count`}
                  setFieldValue={setFieldValue}
                  selectedId={isProfessionalSoftRequired ? undefined : count}
                  defaultPreviewText={defaultPreviewText}
                  isSoftRequired={isProfessionalSoftRequired}
                  testId={`professionals-dropdown-${professional.name}`}
                />
              );
            })}
          </DynamicFormStack>
        </>
      )}
      {professionalList.length > 0 && (socials?.length ?? 0) > 0 && (
        <Divider my={{ base: 4 }} />
      )}
      {socialList.length && (
        <>
          <HStack>
            <Text fontSize="l" mb="2">
              <Trans>Social Activities</Trans>
            </Text>
          </HStack>
          <DynamicFormStack testId="preview-social-form">
            {socialList.map((social, index) => {
              const existingSocial =
                socials && Array.isArray(socials)
                  ? socials.find((s) => s.activityId === social.id)
                  : undefined;
              const count = existingSocial?.count ?? 0;
              const isSocialSoftRequired =
                isSoftRequired && !hasAnySocialSelected && count === 0;
              const defaultPreviewText = isSocialSoftRequired
                ? i18n._("Originally not selected")
                : undefined;
              return (
                <PreviewDropdownFormControl
                  key={index}
                  id={`social${index}`}
                  label={`${social.name} ${formatDateForInput(social.date)}`}
                  isInactive={isEditMode && !social.enabled}
                  values={[
                    { id: 0, leftText: i18n._("Not ordered") },
                    {
                      id: 1,
                      leftText: i18n._("1 person"),
                      rightText: `${formatPrice(social.price)} ${currency}`,
                    },
                    {
                      id: 2,
                      leftText: i18n._("2 person"),
                      rightText: `${formatPrice(social.price * 2)} ${currency}`,
                    },
                    {
                      id: 3,
                      leftText: i18n._("3 person"),
                      rightText: `${formatPrice(social.price * 3)} ${currency}`,
                    },
                  ]}
                  fieldName={`socials[${index}].count`}
                  setFieldValue={setFieldValue}
                  selectedId={isSocialSoftRequired ? undefined : count}
                  defaultPreviewText={defaultPreviewText}
                  isSoftRequired={isSocialSoftRequired}
                  testId={`socials-dropdown-${social.name}`}
                />
              );
            })}
          </DynamicFormStack>
        </>
      )}
      {(areSocialsLoading || areProfessionalsLoading) && (
        <>
          <Skeleton height="50px" />
          <SkeletonText mt="4" noOfLines={2} spacing="4" skeletonHeight="2" />
          <Skeleton height="50px" />
        </>
      )}
    </Stack>
  );
};

export default Programs;
